import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Container } from "react-bootstrap";
import { FaUser, FaClock, FaLock } from "react-icons/fa";
import { WarningCallout, BackLink, Card, SummaryList, Button, Input, ErrorSummary } from "nhsuk-react-components";
import { getPatientEmergencyDetails, verifyEmergencyAccess } from "../../actions/patientActions";
import { listAetiologiesByIds } from '../../actions/aetiologyActions';
import { listEcgsByIds } from '../../actions/ecgActions';
import { listSymptomsByIds } from '../../actions/symptomActions';
import { listFollowupCentresByIds } from '../../actions/followupCentreActions';
import { listImplantCentresByIds } from '../../actions/implantCentreActions';
import { listConsentsByIds } from "../../actions/consentActions";
import Modal from "../../components/modal/Modal.component";
import ImplantInstanceListReadOnly from "../../components/implantInstanceList/ImplantInstanceListReadOnly.component";
import Checklist from "../../components/checklist/Checklist.component";
import { convertToComponentDate } from "../../utils/functions";
import { getStoredEmergencyToken, storeEmergencyToken, removeEmergencyToken } from "../../utils/emergencyAccessStorage";

export default function PatientEmergencyAccessScreen({ match }) {
    const dispatch = useDispatch();
    const patientId = match.params._id;

    const excludedKeys = ["id", "patient", "patientConsents", "patientSymtoms", "patientAetiologies", "patientEcgs", "patientImplantCentres", "patientFollowUpCentres"];

    // Verification gate state
    const [verified, setVerified] = useState(false);
    const [emergencyToken, setEmergencyToken] = useState(null);
    const [surname, setSurname] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [email, setEmail] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState(null);

    // Patient data state
    const [patient, setPatient] = useState(null);
    const [patientDob, setPatientDob] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [genericModalShow, setGenericModalShow] = useState(false);
    const [genericModalContent, setGenericModalContent] = useState(null);

    const patientDetails = useSelector((state) => state.patientDetails);
    const { patient: fetchedPatient } = patientDetails;

    const aetiologyListByIDs = useSelector((state) => state.aetiologyListByIDs);
    const { aetiologies } = aetiologyListByIDs;
    const ecgListByIDs = useSelector((state) => state.ecgListByIDs);
    const { ecgs } = ecgListByIDs;
    const symptomListByIDs = useSelector((state) => state.symptomListByIDs);
    const { symptoms } = symptomListByIDs;
    const followupCentreListByIDs = useSelector((state) => state.followupCentreListByIDs);
    const { followupCentres } = followupCentreListByIDs;
    const implantCentreListByIDs = useSelector((state) => state.implantCentreListByIDs);
    const { implantCentres } = implantCentreListByIDs;
    const consentListByIDs = useSelector((state) => state.consentListByIDs);
    const { consents } = consentListByIDs;

    // On mount: check for a stored valid token
    useEffect(() => {
        document.title = "Emergency Access";
        const stored = getStoredEmergencyToken(patientId);
        if (stored) {
            setEmergencyToken(stored);
            setVerified(true);
        }
    }, [patientId]);

    // Fetch patient data once we have a valid token
    const fetchPatient = useCallback(async (token) => {
        setLoading(true);
        setFetchError(null);
        try {
            await dispatch(getPatientEmergencyDetails(patientId, token));
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Failed to load patient data';
            // If token rejected (expired server-side), clear it and drop back to the form
            if (err?.response?.status === 401) {
                removeEmergencyToken(patientId);
                setVerified(false);
                setEmergencyToken(null);
            }
            setFetchError(msg);
            setLoading(false);
        }
    }, [dispatch, patientId]);

    useEffect(() => {
        if (verified && emergencyToken) {
            fetchPatient(emergencyToken);
        }
    }, [verified, emergencyToken, fetchPatient]);

    useEffect(() => {
        if (fetchedPatient && fetchedPatient.id) {
            setPatient(fetchedPatient);
            setPatientDob(convertToComponentDate(fetchedPatient.dateOfBirth));
            dispatch(listAetiologiesByIds(fetchedPatient.aetiologyIds));
            dispatch(listEcgsByIds(fetchedPatient.ecgIds));
            dispatch(listSymptomsByIds(fetchedPatient.symptomIds));
            dispatch(listFollowupCentresByIds(fetchedPatient.followUpCentreIds));
            dispatch(listImplantCentresByIds(fetchedPatient.implantCentreIds));
            dispatch(listConsentsByIds(fetchedPatient.consentIds));
            setLoading(false);
        }
    }, [dispatch, fetchedPatient]);

    const handleVerify = async (e) => {
        e.preventDefault();
        setVerifyError(null);
        if (!surname.trim() || !dateOfBirth) {
            setVerifyError('Please enter both surname and date of birth.');
            return;
        }
        setVerifying(true);
        try {
            const result = await dispatch(verifyEmergencyAccess(
                patientId,
                surname.trim(),
                new Date(dateOfBirth).toISOString(),
                email.trim() || null
            ));
            storeEmergencyToken(patientId, result.token, result.expiresAt);
            setEmergencyToken(result.token);
            setVerified(true);
        } catch (err) {
            setVerifyError(
                err?.response?.data?.detail ||
                'Verification failed. Please check the surname and date of birth.'
            );
            setVerifying(false);
        }
    };

    const handleEntityClick = (entity) => {
        if (!entity) return;
        setGenericModalContent(entity);
        setGenericModalShow(true);
    };

    const closeGenericModal = () => setGenericModalShow(false);

    // ── Verification gate ─────────────────────────────────────────────────────
    if (!verified) {
        return (
            <div className="nhsuk-width-container">
                <main className="nhsuk-main-wrapper" id="maincontent" role="main">
                    <Container fluid>
                        <div style={{
                            background: '#da291c', color: 'white',
                            padding: '16px', marginBottom: '24px',
                            borderRadius: '4px', display: 'flex',
                            alignItems: 'center', gap: '12px'
                        }}>
                            <FaLock size={24} />
                            <div>
                                <h1 style={{ color: 'white', margin: '0', fontSize: '1.5rem' }}>Emergency Patient Access</h1>
                                <p style={{ margin: '0', fontSize: '0.9rem' }}>Identity verification required</p>
                            </div>
                        </div>

                        <WarningCallout label="Warning callout">
                            <WarningCallout.Label>Confidential Patient Information</WarningCallout.Label>
                            <p>
                                To access this patient's emergency cardiac record, please confirm the patient's
                                surname and date of birth. Unauthorized access is strictly prohibited and will be audited.
                            </p>
                        </WarningCallout>

                        <Card>
                            <Card.Content>
                                <Card.Heading>Verify Patient Identity</Card.Heading>

                                {verifyError && (
                                    <ErrorSummary aria-labelledby="verify-error-title" role="alert">
                                        <ErrorSummary.Title id="verify-error-title">
                                            Verification failed
                                        </ErrorSummary.Title>
                                        <ErrorSummary.Body>
                                            <p>{verifyError}</p>
                                        </ErrorSummary.Body>
                                    </ErrorSummary>
                                )}

                                <form onSubmit={handleVerify}>
                                    <div className="nhsuk-form-group">
                                        <label className="nhsuk-label" htmlFor="surname">
                                            <strong>Patient surname</strong>
                                        </label>
                                        <Input
                                            id="surname"
                                            name="surname"
                                            type="text"
                                            value={surname}
                                            onChange={(e) => setSurname(e.target.value)}
                                            disabled={verifying}
                                            autoComplete="off"
                                        />
                                    </div>

                                    <div className="nhsuk-form-group">
                                        <label className="nhsuk-label" htmlFor="dob">
                                            <strong>Patient date of birth</strong>
                                        </label>
                                        <span className="nhsuk-hint">For example, 15 03 1984</span>
                                        <input
                                            id="dob"
                                            className="nhsuk-input nhsuk-date-input__input nhsuk-input--width-10"
                                            name="dob"
                                            type="date"
                                            value={dateOfBirth}
                                            onChange={(e) => setDateOfBirth(e.target.value)}
                                            disabled={verifying}
                                        />
                                    </div>

                                    <div className="nhsuk-form-group">
                                        <label className="nhsuk-label" htmlFor="email">
                                            <strong>Your email address</strong>
                                        </label>
                                        <span className="nhsuk-hint">Optional — for audit purposes only</span>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            disabled={verifying}
                                            autoComplete="email"
                                        />
                                    </div>

                                    <Button type="submit" disabled={verifying}>
                                        {verifying ? 'Verifying…' : 'Confirm & access record'}
                                    </Button>
                                </form>
                            </Card.Content>
                        </Card>

                        <BackLink href="/">Return to home</BackLink>
                    </Container>
                </main>
            </div>
        );
    }

    // ── Loading / error states ────────────────────────────────────────────────
    if (loading) return <div style={{ padding: '2rem' }}>Loading patient data…</div>;

    if (fetchError) {
        return (
            <div className="nhsuk-width-container">
                <main className="nhsuk-main-wrapper" id="maincontent" role="main">
                    <Container fluid>
                        <h1>Access Denied</h1>
                        <div className="nhsuk-error-summary" aria-labelledby="error-summary-title" role="alert">
                            <h2 className="nhsuk-error-summary__title" id="error-summary-title">Error Loading Patient Data</h2>
                            <div className="nhsuk-error-summary__body">
                                <p>{fetchError}</p>
                            </div>
                        </div>
                        <BackLink href="/">Return to Home</BackLink>
                    </Container>
                </main>
            </div>
        );
    }

    if (!patient) return <div style={{ padding: '2rem' }}>No patient data available.</div>;

    // ── Patient record ────────────────────────────────────────────────────────
    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <div style={{
                        background: '#da291c', color: 'white',
                        padding: '16px', marginBottom: '24px',
                        borderRadius: '4px', display: 'flex',
                        alignItems: 'center', gap: '12px'
                    }}>
                        <FaClock size={24} />
                        <div>
                            <h1 style={{ color: 'white', margin: '0', fontSize: '1.5rem' }}>Emergency Access</h1>
                            <p style={{ margin: '0', fontSize: '0.9rem' }}>Temporary emergency access to patient information</p>
                        </div>
                    </div>
                    <WarningCallout label="Warning callout">
                        <WarningCallout.Label>Important Note</WarningCallout.Label>
                        <p>Unauthorized access to emergency patient data is strictly prohibited. Clinicians, ensure that your code usage is solely for emergency purposes. Any misuse or unauthorized access will result in severe consequences. Prioritize patient privacy and adhere to ethical coding practices.</p>
                    </WarningCallout>
                    <BackLink href="#" onClick={() => window.location.href = '/'}>Go back</BackLink>
                    <div className="nhsuk-grid-row">
                        <div className="nhsuk-grid-column-one-half">
                            <Card>
                                <Card.Content>
                                    <Card.Heading className="nhsuk-heading-m">
                                        <FaUser size={30} />
                                        {patient.forename} {patient.surname}
                                    </Card.Heading>
                                    <SummaryList>
                                        <SummaryList.Row>
                                            <SummaryList.Key>NHS Number</SummaryList.Key>
                                            <SummaryList.Value>{patient.nhsNumber}</SummaryList.Value>
                                        </SummaryList.Row>
                                        <SummaryList.Row>
                                            <SummaryList.Key>Date of Birth</SummaryList.Key>
                                            <SummaryList.Value>{patientDob?.day}/{patientDob?.month}/{patientDob?.year}</SummaryList.Value>
                                        </SummaryList.Row>
                                        <SummaryList.Row>
                                            <SummaryList.Key>Abandoned Hardware</SummaryList.Key>
                                            <SummaryList.Value>{patient.abondonedHardware === true ? "Yes" : patient.abondonedHardware === false ? "No" : "Unknown"}</SummaryList.Value>
                                        </SummaryList.Row>
                                        <SummaryList.Row>
                                            <SummaryList.Key>MR Conditional</SummaryList.Key>
                                            <SummaryList.Value>{patient.mrConditional ? "Yes" : "No"}</SummaryList.Value>
                                        </SummaryList.Row>
                                    </SummaryList>
                                    {[
                                        { title: "Aetiologies", items: aetiologies },
                                        { title: "Symptoms", items: symptoms },
                                        { title: "ECGs", items: ecgs },
                                        { title: "Implant Centres", items: implantCentres },
                                        { title: "Followup Centres", items: followupCentres }
                                    ].map(({ title, items }) => (
                                        <SummaryList.Row key={title}>
                                            <SummaryList.Key>{title}</SummaryList.Key>
                                            <SummaryList.Value>
                                                <ul>
                                                    {items?.map(item => (
                                                        <li key={item.id}>
                                                            <Link to="#" onClick={() => handleEntityClick(item)}>
                                                                {item.name}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </SummaryList.Value>
                                        </SummaryList.Row>
                                    ))}
                                </Card.Content>
                            </Card>
                        </div>
                        <div className="nhsuk-grid-column-one-half">
                            <Card style={{ padding: "30px" }}>
                                <Card.Heading className="nhsuk-heading-m">Implants</Card.Heading>
                                <Card.Description>
                                    <ImplantInstanceListReadOnly patientId={patient.id} />
                                    <div className="nhsuk-u-padding-top-3"></div>
                                    <h3>Patient consents</h3>
                                    <Checklist name="checklist" hint="Consent information." heading="This patient allows:" expand={true}>
                                        {consents?.map(consent => (
                                            <Checklist.Item
                                                key={consent.id}
                                                mode="success"
                                                id={consent.id}
                                                name={consent.consentType}
                                            >
                                                <Link to="#" onClick={() => handleEntityClick(consent)}>
                                                    {consent.consentType}
                                                </Link>
                                            </Checklist.Item>
                                        ))}
                                    </Checklist>
                                </Card.Description>
                            </Card>
                        </div>
                    </div>
                </Container>
            </div>
            <Modal show={genericModalShow} handleClose={closeGenericModal}>
                {genericModalContent && (
                    <SummaryList>
                        {Object.entries(genericModalContent).map(([key, value]) => (
                            !excludedKeys.includes(key) && (
                                <SummaryList.Row key={key}>
                                    <SummaryList.Key>
                                        {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim()}
                                    </SummaryList.Key>
                                    <SummaryList.Value>
                                        {value ? value.toString() : "-"}
                                    </SummaryList.Value>
                                </SummaryList.Row>
                            )
                        ))}
                    </SummaryList>
                )}
            </Modal>
        </React.Fragment>
    );
}
